// src/pages/Scoreboard/ScoreboardUserPage.jsx
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ScoreboardUser from "../../components/ScoreboardUser/ScoreboardUser";

const TIME_PRESETS = [
  { label: "1 min 30", value: 90 },
  { label: "2 min", value: 120 },
  { label: "3 min", value: 180 },
  { label: "4 min", value: 240 },
  { label: "5 min", value: 300 },
];

const formatDuration = (seconds) => {
  const total = Number(seconds) || 0;
  const min = Math.floor(total / 60);
  const sec = total % 60;

  if (sec === 0) return `${min} min`;
  return `${min} min ${String(sec).padStart(2, "0")}`;
};

const ScoreboardUserPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const initialSettings = {
    temps: 180,
    tapis: "1",
    finIppon: true,
    finDeuxIppon: false,
    finPoints: false,
    pointsFin: 10,
    wazariAwaseteIppon: true,
  };

  const [settingsDraft, setSettingsDraft] = useState(initialSettings);
  const [settings, setSettings] = useState(initialSettings);

  const [showSetupPanel, setShowSetupPanel] = useState(true);

  const [redName, setRedName] = useState("Judoka Rouge");
  const [whiteName, setWhiteName] = useState("Judoka Blanc");

  const applySettings = () => {
    setSettings(settingsDraft);
    setShowSetupPanel(false);
  };

  const competiteurRouge = useMemo(
    () => ({
      nom_competiteur: redName?.trim() || "Judoka Rouge",
      prenom_competiteur: "",
      club_competiteur: "",
    }),
    [redName]
  );

  const competiteurBlanc = useMemo(
    () => ({
      nom_competiteur: whiteName?.trim() || "Judoka Blanc",
      prenom_competiteur: "",
      club_competiteur: "",
    }),
    [whiteName]
  );

  const rulesSummary = useMemo(() => {
    const rules = [];

    if (settings.finIppon) rules.push("Fin Ippon");
    if (settings.finDeuxIppon) rules.push("2 Ippons");
    if (settings.finPoints) rules.push(`${settings.pointsFin} pts`);
    if (settings.wazariAwaseteIppon) rules.push("Waza-ari awasete Ippon");

    return rules.length ? rules.join(" · ") : "Aucune règle de fin automatique";
  }, [settings]);

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-950 via-black to-slate-950 text-white">
      <div className="mx-auto flex min-h-screen w-full max-w-none flex-col px-3 pb-4 pt-[130px] sm:px-4">
        {/* Header compact */}
        <div className="mb-3 flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-950/90 p-3 shadow-xl shadow-black/30 backdrop-blur md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-black tracking-tight sm:text-xl">
                🥋 Tableau de Score Visiteur
              </h1>

              <span className="rounded-full border border-blue-400/30 bg-blue-500/10 px-2.5 py-1 text-xs font-semibold text-blue-200">
                Gratuit
              </span>

              {sessionId && (
                <span className="rounded-full border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-300">
                  Session : {sessionId}
                </span>
              )}
            </div>

            <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-300">
              <span className="rounded-full bg-slate-900 px-2.5 py-1">
                Tapis {settings.tapis || "1"}
              </span>

              <span className="rounded-full bg-slate-900 px-2.5 py-1">
                ⏱ {formatDuration(settings.temps)}
              </span>

              <span className="rounded-full bg-red-950/70 px-2.5 py-1 text-red-100">
                Rouge : {competiteurRouge.nom_competiteur}
              </span>

              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-900">
                Blanc : {competiteurBlanc.nom_competiteur}
              </span>

              <span className="rounded-full bg-slate-900 px-2.5 py-1">
                {rulesSummary}
              </span>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowSetupPanel((v) => !v)}
              className="rounded-xl border border-blue-400/40 bg-blue-600 px-4 py-2 text-sm font-bold text-white shadow-lg shadow-blue-950/40 transition hover:bg-blue-500"
            >
              {showSetupPanel ? "Masquer les réglages" : "Afficher les réglages"}
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-slate-800"
            >
              Retour
            </button>
          </div>
        </div>

        {/* Panneau réglages */}
        {showSetupPanel && (
          <div className="mb-4 rounded-3xl border border-slate-800 bg-slate-900/95 p-4 shadow-2xl shadow-black/40">
            <div className="mb-4 flex flex-col gap-1">
              <h2 className="text-lg font-black">⚙️ Configuration du combat</h2>
              <p className="text-sm text-slate-400">
                Réglez le tapis, la durée, les règles de fin automatique et les noms affichés.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[0.7fr_0.9fr_1.3fr_1fr]">
              {/* Tapis */}
              <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                <label className="mb-2 block text-sm font-semibold text-slate-200">
                  Numéro du tapis
                </label>

                <input
                  type="text"
                  value={settingsDraft.tapis}
                  onChange={(e) =>
                    setSettingsDraft((p) => ({
                      ...p,
                      tapis: e.target.value,
                    }))
                  }
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-center text-lg font-black text-white outline-none transition focus:border-blue-400"
                  placeholder="1"
                />
              </div>

              {/* Durée */}
              <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                <label className="mb-2 block text-sm font-semibold text-slate-200">
                  Durée de combat
                </label>

                <select
                  value={settingsDraft.temps}
                  onChange={(e) =>
                    setSettingsDraft((p) => ({
                      ...p,
                      temps: Number(e.target.value || 0),
                    }))
                  }
                  className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none transition focus:border-blue-400"
                >
                  {TIME_PRESETS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                <p className="mt-2 text-xs text-slate-500">
                  Durées usuelles : 1’30, 2’, 3’, 4’ ou 5’.
                </p>
              </div>

              {/* Règles */}
              <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                <p className="mb-3 text-sm font-semibold text-slate-200">
                  Règles de fin automatique
                </p>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={settingsDraft.finIppon}
                      onChange={(e) =>
                        setSettingsDraft((p) => ({
                          ...p,
                          finIppon: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 accent-blue-500"
                    />
                    Fin sur Ippon
                  </label>

                  <label className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={settingsDraft.finDeuxIppon}
                      onChange={(e) =>
                        setSettingsDraft((p) => ({
                          ...p,
                          finDeuxIppon: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 accent-blue-500"
                    />
                    Fin sur 2 Ippons
                  </label>

                  <label className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={settingsDraft.wazariAwaseteIppon}
                      onChange={(e) =>
                        setSettingsDraft((p) => ({
                          ...p,
                          wazariAwaseteIppon: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 accent-blue-500"
                    />
                    Waza-ari awasete Ippon
                  </label>

                  <div className="rounded-xl border border-slate-800 bg-slate-900 px-3 py-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={settingsDraft.finPoints}
                        onChange={(e) =>
                          setSettingsDraft((p) => ({
                            ...p,
                            finPoints: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 accent-blue-500"
                      />
                      Fin sur points
                    </label>

                    <div className="mt-2 flex items-center gap-2 pl-6">
                      <span className="text-xs text-slate-400">Seuil</span>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={settingsDraft.pointsFin}
                        disabled={!settingsDraft.finPoints}
                        onChange={(e) =>
                          setSettingsDraft((p) => ({
                            ...p,
                            pointsFin: Number(e.target.value || 0),
                          }))
                        }
                        className="w-20 rounded-lg border border-slate-700 bg-slate-950 px-2 py-1 text-center text-sm text-white outline-none transition focus:border-blue-400 disabled:cursor-not-allowed disabled:opacity-40"
                      />
                      <span className="text-xs text-slate-400">pts</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Noms */}
              <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                <p className="mb-3 text-sm font-semibold text-slate-200">
                  Combattants affichés
                </p>

                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-red-200">
                      Judoka Rouge
                    </label>
                    <input
                      type="text"
                      value={redName}
                      onChange={(e) => setRedName(e.target.value)}
                      className="w-full rounded-xl border border-red-900/60 bg-slate-950 px-3 py-2 text-sm text-white outline-none transition focus:border-red-400"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-200">
                      Judoka Blanc
                    </label>
                    <input
                      type="text"
                      value={whiteName}
                      onChange={(e) => setWhiteName(e.target.value)}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none transition focus:border-blue-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={() => setShowSetupPanel(false)}
                className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:bg-slate-800"
              >
                Fermer sans appliquer
              </button>

              <button
                type="button"
                onClick={applySettings}
                className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-black text-white shadow-lg shadow-blue-950/40 transition hover:bg-blue-500"
              >
                Appliquer et masquer
              </button>
            </div>
          </div>
        )}

        {/* Tableau */}
        <div className="w-full flex-1 overflow-hidden rounded-3xl border border-slate-800 bg-black shadow-2xl shadow-black">
          <ScoreboardUser
            settings={settings}
            competiteur1={competiteurRouge}
            competiteur2={competiteurBlanc}
            soundKey={sessionId || "public"}
          />
        </div>
      </div>
    </div>
  );
};

export default ScoreboardUserPage;